import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { FiTrash2, FiX } from 'react-icons/fi'
import type { CreateWebinarSuggestionInput } from '@domain/models/webinar-suggestions'

const TEXT_INPUT_CLASS =
  'w-full rounded-2xl border border-border bg-background px-5 py-3.5 text-base font-semibold text-text-primary transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-2xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!'
const TEXTAREA_CLASS = `${TEXT_INPUT_CLASS} min-h-38 resize-y`
const SUBJECT_MAX_LENGTH = 80
const DESCRIPTION_MAX_LENGTH = 400
const ADMIN_REPLY_LABEL_MAX_LENGTH = 40

interface WebinarSuggestionModalProps {
  isOpen: boolean
  mode: 'create' | 'edit'
  initialValue?: {
    subject: string
    description: string
    adminReplyLabel?: string
  }
  isSubmitting: boolean
  error: string | null
  onSubmit: (input: CreateWebinarSuggestionInput) => void
  onClose: () => void
  showAdminReplyLabel?: boolean
}

export default function WebinarSuggestionModal({
  isOpen,
  mode,
  initialValue,
  isSubmitting,
  error,
  onSubmit,
  onClose,
  showAdminReplyLabel = false,
}: WebinarSuggestionModalProps) {
  const [subject, setSubject] = useState(initialValue?.subject ?? '')
  const [description, setDescription] = useState(initialValue?.description ?? '')
  const [adminReplyLabel, setAdminReplyLabel] = useState(
    initialValue?.adminReplyLabel ?? '',
  )

  const isEditMode = mode === 'edit'

  useEffect(() => {
    if (!isOpen) {
      return
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isSubmitting, onClose])

  const submitLabel = useMemo(() => {
    if (isSubmitting) {
      return isEditMode ? 'Saving...' : 'Submitting...'
    }
    return isEditMode ? 'Save Changes' : 'Submit Suggestion'
  }, [isEditMode, isSubmitting])
  const subjectRemaining = SUBJECT_MAX_LENGTH - subject.length
  const descriptionRemaining = DESCRIPTION_MAX_LENGTH - description.length
  const adminReplyLabelRemaining = ADMIN_REPLY_LABEL_MAX_LENGTH - adminReplyLabel.length

  if (!isOpen) {
    return null
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSubmit({
      subject,
      description,
      adminReplyLabel: showAdminReplyLabel ? adminReplyLabel : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-60 bg-black/55">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close webinar suggestion modal"
        onClick={onClose}
        disabled={isSubmitting}
      />

      <div className="relative z-10 flex h-full w-full items-center justify-center p-4 md:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label={isEditMode ? 'Edit webinar suggestion' : 'Submit webinar suggestion'}
          className="relative w-full max-w-3xl rounded-4xl bg-surface p-7 shadow-2xl sm:p-10"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close webinar suggestion modal"
            disabled={isSubmitting}
            className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-background disabled:cursor-not-allowed disabled:opacity-70"
          >
            <FiX className="h-6 w-6" />
          </button>

          <h3 className="pr-10 text-4xl font-bold tracking-tight text-text-primary">
            {isEditMode ? 'Edit Webinar Suggestion' : 'Suggest a Webinar'}
          </h3>
          <p className="mt-2 text-lg text-text-secondary">
            Share a webinar subject and short description with the community.
          </p>

          <form onSubmit={handleSubmit} className="mt-8">
            <label htmlFor="webinar-suggestion-subject" className="text-sm font-bold tracking-[0.12em] text-primary uppercase">
              Subject
            </label>
            <input
              id="webinar-suggestion-subject"
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className={`${TEXT_INPUT_CLASS} mt-3`}
              placeholder="e.g., Nutrition and Parkinson's daily management"
              required
              disabled={isSubmitting}
              maxLength={SUBJECT_MAX_LENGTH}
            />
            <p className="mt-1 text-right text-xs font-semibold text-text-muted">
              {subjectRemaining} characters left
            </p>

            <label
              htmlFor="webinar-suggestion-description"
              className="mt-7 block text-sm font-bold tracking-[0.12em] text-primary uppercase"
            >
              Description
            </label>
            <textarea
              id="webinar-suggestion-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className={`${TEXTAREA_CLASS} mt-3`}
              placeholder="Add a brief description for what you would like to learn."
              required
              disabled={isSubmitting}
              maxLength={DESCRIPTION_MAX_LENGTH}
            />
            <p className="mt-1 text-right text-xs font-semibold text-text-muted">
              {descriptionRemaining} characters left
            </p>

            {showAdminReplyLabel ? (
              <>
                <label
                  htmlFor="webinar-suggestion-admin-reply-label"
                  className="mt-7 block text-sm font-bold tracking-[0.12em] text-primary uppercase"
                >
                  Admin Reply Label
                </label>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    id="webinar-suggestion-admin-reply-label"
                    type="text"
                    value={adminReplyLabel}
                    onChange={(event) => setAdminReplyLabel(event.target.value)}
                    className={TEXT_INPUT_CLASS}
                    placeholder="e.g., Watch it now"
                    disabled={isSubmitting}
                    maxLength={ADMIN_REPLY_LABEL_MAX_LENGTH}
                  />
                  <button
                    type="button"
                    onClick={() => setAdminReplyLabel('')}
                    disabled={isSubmitting || adminReplyLabel.length === 0}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-border-light text-text-secondary transition-colors hover:bg-border-light disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Clear admin reply label"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-right text-xs font-semibold text-text-muted">
                  {adminReplyLabelRemaining} characters left
                </p>
              </>
            ) : null}

            {error ? (
              <p className="mt-4 rounded-xl border border-border-light bg-background px-3 py-2 text-sm font-semibold text-text-secondary">
                {error}
              </p>
            ) : null}

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <button
                type="submit"
                disabled={isSubmitting || subject.trim().length === 0 || description.trim().length === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue px-4 py-3 text-sm font-semibold text-white shadow-sm transition-[transform,opacity,background-color] duration-200 hover:scale-[1.02] hover:bg-blue/90 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
              >
                {submitLabel}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-2 py-2 text-sm font-semibold text-blue transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  )
}
